/**
 * This page provides an overview of the launch queues for either one entity
 * or a combined queue overview across the user's teams and personal queues.
 */

import {Alert} from '@wandb/weave/components/Alert';
import * as Tabs from '@wandb/weave/components/Tabs';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router';

import {setDocumentTitle} from '../../../util/document';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {usePublicQueueAccess} from '../hooks';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../launchActive';
import {CreateQueueDrawer} from './CreateQueueDrawer';
import {useQueues} from './hooks';
import {Queues} from './Queues';
import * as S from './QueuesPage.styles';
import {QueuesPageHeader} from './QueuesPageHeader';
import {TabPublicJobs} from './TabPublicJobs';
import {ZeroQueues} from './ZeroQueues';

type QueuesPageParams = {
  entityName?: string;
};

export const QueuesPage = () => {
  const {entityName} = useParams<QueuesPageParams>();

  const pageTitle = entityName ? `Launch - ${entityName}` : 'Launch';
  useEffect(() => {
    setDocumentTitle(pageTitle);
  }, [pageTitle]);

  const [isCreatingQueue, setIsCreatingQueue] = useState(false);
  const toggleDrawer = () => {
    setIsCreatingQueue(!isCreatingQueue);
  };

  // Entity that a queue will be created in.
  // TODO: Should we select an entity by default when queues are unfiltered?
  const createEntity = entityName ?? '';
  const {queues, error, loading, refetch: refetchQueues} = useQueues();

  const {
    isLaunchActive,
    loading: isLaunchActiveLoading,
    error: isLaunchActiveError,
  } = useAccountOrganizationOrPersonalEntityUsingLaunch();

  const {hasPublicQueueAccess, isFeatureFlagLoading, featureFlagQueryError} =
    usePublicQueueAccess();

  const hasAnyQueryErrored =
    error || isLaunchActiveError || featureFlagQueryError;

  const isAnyQueryLoading =
    loading || isLaunchActiveLoading || isFeatureFlagLoading;

  if (hasAnyQueryErrored) {
    return <Alert severity="error">Error loading queue information</Alert>;
  }
  if (isAnyQueryLoading) {
    return <Loader name="queues-page-loader" size="huge" />;
  }

  return (
    <Tailwind>
      <S.Panel>
        <CreateQueueDrawer
          open={isCreatingQueue}
          toggleDrawer={toggleDrawer}
          entityName={createEntity}
          refetchQueues={refetchQueues}
        />
        <QueuesPageHeader
          onCreateQueue={toggleDrawer}
          showCreateQueueButton={!hasPublicQueueAccess && queues.length !== 0}
        />
        {hasPublicQueueAccess && isLaunchActive && <QueuesPageTabs />}
        {hasPublicQueueAccess && !isLaunchActive && (
          <>
            <div className="text-xl text-moon-800 font-semibold mb-16 pt-16 border-t border-moon-250">
              W&B Public Jobs
            </div>
            <TabPublicJobs />
          </>
        )}
        {!hasPublicQueueAccess &&
          (queues.length === 0 ? (
            <ZeroQueues onCreateQueue={toggleDrawer} />
          ) : (
            <>
              <div className="text-xl text-moon-800 font-semibold mb-16 pt-16 border-t border-moon-250">
                Queues
              </div>
              <Queues
                queues={queues}
                refetchQueues={refetchQueues}
                showEntityName={!entityName}
              />
            </>
          ))}
      </S.Panel>
    </Tailwind>
  );
};

export enum QueuesPageTab {
  QUEUES = 'queues',
  PUBLIC_JOBS = 'public jobs',
}

export const QUEUES_PAGE_TAB_LABELS = {
  [QueuesPageTab.QUEUES]: 'Queues',
  [QueuesPageTab.PUBLIC_JOBS]: 'W&B public jobs',
} as const;

const QueuesPageTabs = () => {
  const [selectedTab, setSelectedTab] = useState<QueuesPageTab>(
    QueuesPageTab.QUEUES
  );

  const onTabChange = (newValue: string) => {
    setSelectedTab(newValue as QueuesPageTab);
  };

  const {queues, refetch: refetchQueues} = useQueues();
  const {entityName} = useParams<QueuesPageParams>();

  return (
    <Tabs.Root value={selectedTab} onValueChange={onTabChange}>
      <Tabs.List>
        <Tabs.Trigger value={QueuesPageTab.QUEUES}>
          {QUEUES_PAGE_TAB_LABELS[QueuesPageTab.QUEUES]}
        </Tabs.Trigger>
        <Tabs.Trigger value={QueuesPageTab.PUBLIC_JOBS}>
          {QUEUES_PAGE_TAB_LABELS[QueuesPageTab.PUBLIC_JOBS]}
        </Tabs.Trigger>
      </Tabs.List>
      <div className="mt-16" />
      <Tabs.Content value={QueuesPageTab.QUEUES}>
        <Queues
          queues={queues}
          refetchQueues={refetchQueues}
          showEntityName={!entityName}
        />
      </Tabs.Content>
      <Tabs.Content value={QueuesPageTab.PUBLIC_JOBS}>
        <TabPublicJobs />
      </Tabs.Content>
    </Tabs.Root>
  );
};
